import type {
  CommonAddress,
  Prediction,
} from '@wix/ambassador-wix-atlas-service-web/types';
import { autocomplete, places } from '@wix/atlas';
import { useCallback, useRef, useState } from 'react';

import useDebouncedCallback from '../../address-input/use-debounced-callback';
import useIsMounted from '../../address-input/use-is-mounted';

type UpdatePredictions = ({ input, countryCodes }: any) => void;
type ClearPredictions = () => void;

type AtlasHeadless = {
  predictions: Prediction[];
  loading: boolean;
  updatePredictions: UpdatePredictions;
  clearPredictions: ClearPredictions;
  getPlace: (searchId: string) => Promise<CommonAddress | undefined>;
};
type AtlasHeadlessParams = {
  onError?: (error: unknown) => void;
  sessionToken: string;
};

const initialFetchState = {
  loading: false,
  predictions: [] as Prediction[],
};

const useAtlasHeadless = ({
  onError,
  sessionToken,
}: AtlasHeadlessParams): AtlasHeadless => {
  const debounceMs = 200;
  const [{ loading, predictions }, setFetchState] = useState(initialFetchState);

  const predictionsRequestId = useRef(0); // id of latest request to avoid race conditions
  const isMounted = useIsMounted(); // checks whether component is still mounted

  const clearPredictions = useCallback(() => {
    // Increase request id counter
    predictionsRequestId.current++;
    setFetchState(initialFetchState);
  }, []);

  const updatePredictions: UpdatePredictions = useDebouncedCallback(
    async ({ input, countryCodes }) => {
      if (!isMounted()) {
        return;
      }

      if (!input) {
        clearPredictions();
        return;
      }

      // Increase request id counter
      const requestId = ++predictionsRequestId.current;

      setFetchState((state) => ({ ...state, loading: true }));

      let newPredictions: Prediction[] | undefined;
      try {
        const response = await autocomplete.predict(input, {
          countryCodes,
          sessionToken,
        });
        newPredictions = response.predictions as unknown as Prediction[];
      } catch (error) {
        // failed to fetch predictions
        if (onError) {
          onError(error);
        } else {
          console.error(error);
        }
      } finally {
        // check if no new fetch request has been initiated
        const isMostRecentRequest = requestId === predictionsRequestId.current;

        if (isMounted() && isMostRecentRequest) {
          setFetchState((state) => ({
            loading: false,
            // set new predictions if fetched properly, else keep current predictions
            predictions: newPredictions || state.predictions,
          }));
        }
      }
    },
    [autocomplete, onError],
    debounceMs,
  );

  const getPlace = useCallback(
    async (searchId: string) => {
      const response = await places.getPlace(searchId, { sessionToken });
      return response.place?.address as CommonAddress;
    },
    [sessionToken],
  );

  return {
    predictions,
    loading,
    updatePredictions,
    clearPredictions,
    getPlace,
  };
};

export default useAtlasHeadless;
